import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api, { mediaUrl, apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { PremiumUpsellCard } from '@/components/PremiumUpsellCard';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Loader2, Clapperboard, Sparkles, RefreshCw, AlertTriangle, Ban, CreditCard, Check } from 'lucide-react';
import { toast } from 'sonner';

const KAIA_PORTRAIT = '/api/static/coach/kaia_portrait.png';

export const KaiaCoachVideo = ({ analysisId, isPro }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pollRef = useRef(null);

  const poll = useCallback((jobId) => {
    clearTimeout(pollRef.current);
    const tick = async () => {
      try {
        const { data } = await api.get(`/coach-videos/${jobId}`);
        setJob(data);
        if (data.status === 'processing') pollRef.current = setTimeout(tick, 4000);
      } catch (e) { /* keep last state */ }
    };
    tick();
  }, []);

  const fetchQuote = useCallback(async () => {
    try {
      const { data } = await api.get(`/analyses/${analysisId}/coach-video/quote`);
      setQuote(data);
      if (data.job && (data.job.status === 'completed' || data.job.status === 'processing')) {
        setJob(data.job);
        if (data.job.status === 'processing') poll(data.job.job_id);
      }
    } catch (e) { /* non-blocking */ }
  }, [analysisId, poll]);

  useEffect(() => { if (isPro) fetchQuote(); }, [isPro, fetchQuote]);
  useEffect(() => () => clearTimeout(pollRef.current), []);

  const doGenerate = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      const { data } = await api.post(`/analyses/${analysisId}/coach-video`);
      setJob(data);
      if (data.status === 'processing') poll(data.job_id);
      fetchQuote();
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (code === 402 || detail === 'out_of_video_credits') {
        toast.error(t('coach.video.outOfCreditsToast'));
      } else if (code === 503) {
        toast.error(t('coach.video.capReachedToast'));
      } else {
        toast.error(apiError(e, t('coach.video.error')));
      }
      fetchQuote();
    } finally { setBusy(false); }
  };

  if (!isPro) {
    return (
      <PremiumUpsellCard testid="upsell-kaia-video" icon={Clapperboard}
        title={t('coach.video.proTitle')} description={t('coach.video.proDesc')} />
    );
  }

  const status = job?.status;
  const reason = quote?.reason;
  const useCredits = quote?.source === 'credits';
  const showVideo = status === 'completed' && job?.video_url;
  const isLocked = !showVideo && status !== 'processing' && (reason === 'disabled' || reason === 'cap_reached');
  const isNoCredits = !showVideo && status !== 'processing' && status !== 'failed' && reason === 'no_credits';
  const showGenerate = !showVideo && status !== 'processing' && status !== 'failed' && !isLocked && !isNoCredits;

  return (
    <div className="kite-card p-5" data-testid="kaia-coach-video">
      <div className="flex items-center gap-3 mb-1">
        <img src={mediaUrl(KAIA_PORTRAIT)} alt="Kaia" className="w-11 h-11 rounded-full object-cover border border-[#00B4D8]/40" />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold flex items-center gap-2"><Clapperboard className="w-4 h-4 text-[#00B4D8]" /> {t('coach.video.title')}</h3>
          <p className="text-xs text-muted-foreground">{t('coach.video.subtitle')}</p>
        </div>
      </div>

      {!showVideo && (
        <p className="mt-3 flex items-center gap-1.5 text-[11px] text-[#2EE59D]" data-testid="kaia-text-free-note">
          <Check className="w-3.5 h-3.5 shrink-0" /> {t('coach.video.textAlwaysFree')}
        </p>
      )}

      {showVideo ? (
        <video src={mediaUrl(job.video_url)} controls playsInline poster={mediaUrl(KAIA_PORTRAIT)}
          className="w-full rounded-xl bg-black mt-3 max-h-[70vh]" data-testid="kaia-video-player" />
      ) : status === 'processing' ? (
        <div className="mt-3 flex flex-col items-center text-center py-7 rounded-xl bg-[#0A0A0A] border border-white/8" data-testid="kaia-video-processing">
          <Loader2 className="w-7 h-7 text-[#00B4D8] animate-spin" />
          <p className="text-sm mt-3 font-medium">{t('coach.video.generating')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('coach.video.generatingHint')}</p>
        </div>
      ) : status === 'failed' ? (
        <div className="mt-3" data-testid="kaia-video-failed">
          <div className="rounded-xl border border-[#FFB020]/40 bg-[#FFB020]/5 p-3 flex items-start gap-2 text-sm">
            <AlertTriangle className="w-4 h-4 text-[#FFB020] mt-0.5 shrink-0" />
            <span>{job.error_code === 'insufficient_credit' ? t('coach.video.noCredit')
              : job.error_code === 'not_configured' ? t('coach.video.notConfigured')
              : t('coach.video.error')}</span>
          </div>
          <Button onClick={() => setConfirmOpen(true)} disabled={busy || !quote} variant="outline" data-testid="kaia-retry-btn"
            className="w-full mt-3 border-white/20 bg-white/5 hover:bg-white/10">
            <RefreshCw className="w-4 h-4 mr-2" /> {t('coach.video.retry')}
          </Button>
        </div>
      ) : isLocked ? (
        <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-4 flex items-start gap-2 text-sm" data-testid="kaia-video-disabled">
          <Ban className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">{t('coach.video.disabledTitle')}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t('coach.video.disabledDesc')}</p>
          </div>
        </div>
      ) : isNoCredits ? (
        <div className="mt-3 rounded-xl border border-[#00B4D8]/30 bg-[#00B4D8]/5 p-4" data-testid="kaia-video-no-credits">
          <p className="font-medium text-sm">{t('coach.video.noCreditsTitle')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('coach.video.noCreditsDesc', { allowed: quote?.quota_allowed ?? 0 })}</p>
          <Button onClick={() => navigate('/pricing')} data-testid="kaia-buy-credits-btn"
            className="w-full mt-3 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl">
            <CreditCard className="w-4 h-4 mr-2" /> {t('coach.video.buyCredits')}
          </Button>
        </div>
      ) : showGenerate ? (
        <>
          {quote && (
            <p className="text-[11px] text-muted-foreground mt-3" data-testid="kaia-quota-line">
              {useCredits
                ? t('coach.video.costCredit', { cost: quote.credit_cost ?? 1, count: quote.video_credits })
                : t('coach.video.costQuota', { remaining: quote.quota_remaining, allowed: quote.quota_allowed })}
            </p>
          )}
          <Button onClick={() => setConfirmOpen(true)} disabled={busy || !quote} data-testid="kaia-generate-btn"
            className="w-full mt-2 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} {t('coach.video.generate')}
          </Button>
        </>
      ) : null}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="kaia-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('coach.video.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {useCredits
                ? t('coach.video.confirmCreditDesc', { cost: quote?.credit_cost ?? 1, count: quote?.video_credits ?? 0 })
                : t('coach.video.confirmQuotaDesc', { remaining: quote?.quota_remaining ?? 0 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-xl border border-[#FFB020]/40 bg-[#FFB020]/10 p-3 flex items-start gap-2 text-sm" data-testid="kaia-confirm-warn">
            <AlertTriangle className="w-4 h-4 text-[#FFB020] mt-0.5 shrink-0" />
            <span className="text-white/90">{t('coach.video.confirmWarn')}</span>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="kaia-confirm-cancel">{t('coach.video.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={doGenerate} data-testid="kaia-confirm-generate"
              className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black">
              {useCredits
                ? t('coach.video.confirmGenerateCredit', { cost: quote?.credit_cost ?? 1 })
                : t('coach.video.confirmGenerate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
